import * as React from "react"
import { Pencil, Check, X, Trash, Plus } from "lucide-react"
import { cn, Input } from "@strivelabs/ui-core"
import { Button, IconButton } from "@strivelabs/ui-core/v2"

/**
 * EditableStringList
 * - Component for editable string lists with two modes: flat and grouped
 * - Supports staged changes with Apply/Cancel workflow
 * - Aligns with the UI Streaming Contract for editable_list blocks
 */
export type EditableStringListGroup = { label?: string; items: string[] }

export type EditableStringListProps = {
  /** Mode: "string" for flat list, "grouped" for grouped list (defaults to "string") */
  mode?: "string" | "grouped"
  /** Items for flat mode */
  items?: string[]
  /** Groups for grouped mode */
  groups?: EditableStringListGroup[]
  /** Called when Apply is clicked (staged mode) */
  onApply?: (data: string[] | EditableStringListGroup[]) => void
  /** Called when Cancel is clicked (staged mode) */
  onCancel?: () => void
  /** Show Add button */
  allowAdd?: boolean
  /** Show Remove action */
  allowRemove?: boolean
  /** Placeholder text for input field */
  inputPlaceholder?: string
  /** Text for Add button */
  addButtonText?: string
  /** Text for Apply button */
  applyButtonText?: string
  /** Text for Cancel button */
  cancelButtonText?: string
  /** Custom class names */
  className?: string
  itemClassName?: string
  actionsClassName?: string
  /** Empty state content */
  emptyState?: React.ReactNode
  /** Disable the CTAs */
  disabled?: boolean
}

export function EditableStringList(props: EditableStringListProps) {
  const {
    mode = "string",
    items = [],
    groups = [],
    onApply,
    onCancel,
    inputPlaceholder = "Enter item",
    allowAdd = true,
    allowRemove = true,
    addButtonText = "Add",
    applyButtonText = "Apply",
    cancelButtonText = "Cancel",
    className,
    itemClassName,
    actionsClassName,
    emptyState,
    disabled = false,
  } = props

  const isGrouped = mode === "grouped"

  const [editingKey, setEditingKey] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<string>("")

  // Local staged state to support Apply/Cancel confirmation UX
  const [stagedItems, setStagedItems] = React.useState<string[]>([...items])
  const [stagedGroups, setStagedGroups] = React.useState<EditableStringListGroup[]>(
    JSON.parse(JSON.stringify(groups))
  )

  // Keep staged state in sync if external props change
  const prevItemsRef = React.useRef<string[]>([...items])
  const prevGroupsRef = React.useRef<EditableStringListGroup[]>(JSON.parse(JSON.stringify(groups)))
  
  React.useEffect(() => {
    // Only update staged state if props actually changed (not just re-renders)
    if (isGrouped) {
      if (JSON.stringify(prevGroupsRef.current) !== JSON.stringify(groups)) {
        setStagedGroups(JSON.parse(JSON.stringify(groups)))
        prevGroupsRef.current = JSON.parse(JSON.stringify(groups))
      }
    } else {
      if (JSON.stringify(prevItemsRef.current) !== JSON.stringify(items)) {
        setStagedItems([...items])
        prevItemsRef.current = [...items]
      }
    }
  }, [isGrouped, items, groups])

  const beginEdit = (key: string, initial: string) => {
    setEditingKey(key)
    setDraft(initial)
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setDraft("")
  }

  const commitEdit = (next: string) => {
    if (!editingKey) return

    if (isGrouped) {
      const m = editingKey.match(/^g:(\d+):i:(\d+)$/)
      if (!m) return
      const gi = Number(m[1])
      const ii = Number(m[2])
      
      const nextGroups = [...stagedGroups]
      nextGroups[gi] = {
        ...nextGroups[gi],
        items: [...nextGroups[gi].items]
      }
      nextGroups[gi].items[ii] = next
      setStagedGroups(nextGroups)
    } else {
      const m = editingKey.match(/^i:(\d+)$/)
      if (!m) return
      const i = Number(m[1])
      
      const nextItems = [...stagedItems]
      nextItems[i] = next
      setStagedItems(nextItems)
    }
    cancelEdit()
  }

  const removeItem = (key: string) => {
    if (isGrouped) {
      const m = key.match(/^g:(\d+):i:(\d+)$/)
      if (!m) return
      const gi = Number(m[1])
      const ii = Number(m[2])
      
      const nextGroups = [...stagedGroups]
      nextGroups[gi] = {
        ...nextGroups[gi],
        items: nextGroups[gi].items.filter((_, idx) => idx !== ii)
      }
      setStagedGroups(nextGroups)
    } else {
      const m = key.match(/^i:(\d+)$/)
      if (!m) return
      const i = Number(m[1])
      
      const nextItems = stagedItems.filter((_, idx) => idx !== i)
      setStagedItems(nextItems)
    }
    
    if (editingKey === key) cancelEdit()
  }

  const addItem = (groupIndex?: number) => {
    if (isGrouped) {
      const gi = groupIndex ?? 0
      const nextGroups = [...stagedGroups]
      nextGroups[gi] = {
        ...nextGroups[gi],
        items: [...nextGroups[gi].items, ""]
      }
      setStagedGroups(nextGroups)
      
      // Start editing the newly added item
      const ii = nextGroups[gi].items.length - 1
      beginEdit(`g:${gi}:i:${ii}`, "")
    } else {
      const nextItems = [...stagedItems, ""]
      setStagedItems(nextItems)
      beginEdit(`i:${nextItems.length - 1}`, "")
    }
  }

  const handleApply = () => {
    if (isGrouped) {
      onApply?.(stagedGroups)
    } else {
      onApply?.(stagedItems)
    }
  }

  const handleCancel = () => {
    // Reset staged state to original props
    if (isGrouped) {
      setStagedGroups(JSON.parse(JSON.stringify(groups)))
    } else {
      setStagedItems([...items])
    }
    cancelEdit()
    onCancel?.()
  }

  // Render a single row (item)
  const RowView = ({
    value,
    editKey,
    isLast,
  }: {
    value: string
    editKey: string
    isLast?: boolean
  }) => {
    const isEditing = editingKey === editKey
    return (
      <div className={cn("px-4 py-3", !isLast && "border-b", itemClassName)} role="listitem">
        <div className="flex items-center gap-2 w-full min-w-0">
          <div className="flex-1 min-w-0 overflow-hidden">
            {isEditing ? (
              <InlineStringEditor
                value={draft}
                placeholder={inputPlaceholder}
                onCancel={cancelEdit}
                onSave={(val) => commitEdit(val)}
                disabled={disabled}
              />
            ) : (
              <div className="text-sm break-words whitespace-normal">
                {value || <span className="text-muted-foreground">(empty)</span>}
              </div>
            )}
          </div>

          {!isEditing && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <IconButton size="sm" onClick={() => beginEdit(editKey, value)} disabled={disabled} aria-label="Edit item">
                <Pencil className="h-4 w-4" />
              </IconButton>
              {allowRemove && (
                <IconButton
                  size="sm"
                  tone="danger"
                  onClick={() => removeItem(editKey)}
                  aria-label="Remove item"
                  disabled={disabled}
                >
                  <Trash className="h-4 w-4" />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Render flat list mode
  if (!isGrouped) {
    return (
      <div className={cn("rounded-md border", className)}>
        {stagedItems.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">{emptyState ?? "No items"}</div>
        )}
        {stagedItems.length > 0 && (
          <div>
            {stagedItems.map((v, i) => (
              <RowView key={`i-${i}`} value={v} editKey={`i:${i}`} isLast={i === stagedItems.length - 1 && !allowAdd} />
            ))}
          </div>
        )}
        {allowAdd && (
          <div className="px-4 py-3">
            <Button variant="secondary" onClick={() => addItem()} className="w-full" disabled={disabled}>
              <Plus className="h-4 w-4" />
              {addButtonText}
            </Button>
          </div>
        )}

        {onApply && (
          <div className={cn("flex items-center justify-end gap-2 px-4 py-3 mt-2 border-t", actionsClassName)}>
            <Button
              variant="secondary"
              size="xs"
              onClick={handleCancel}
              disabled={disabled}
            >
              {cancelButtonText}
            </Button>
            <Button
              size="xs"
              onClick={handleApply}
              disabled={disabled}
            >
              {applyButtonText}
            </Button>
          </div>
        )}
      </div>
    )
  }

  // Render grouped mode
  return (
    <div className={cn("rounded-md border p-2", className)}>
      {stagedGroups.length === 0 && (
        <div className="p-4 text-sm text-muted-foreground">{emptyState ?? "No groups"}</div>
      )}
      {stagedGroups.length > 0 && (
        <div>
          {stagedGroups.map((g, gi) => (
            <div key={`g-${gi}`} className={cn(gi > 0 && "mt-4")}>
              {/* Group header */}
              <div className="bg-muted/50 px-4 py-3 border-b">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{g.label ?? `Group ${gi + 1}`}</div>
                  {allowAdd && (
                    <Button variant="secondary" size="sm" onClick={() => addItem(gi)} disabled={disabled}>
                      <Plus className="h-4 w-4" />
                      {addButtonText}
                    </Button>
                  )}
                </div>
              </div>

              {/* Group items */}
              {g.items.length > 0 && (
                <div>
                  {g.items.map((v, ii) => (
                    <RowView 
                      key={`g-${gi}-i-${ii}`} 
                      value={v} 
                      editKey={`g:${gi}:i:${ii}`}
                      isLast={ii === g.items.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {onApply && (
        <div className={cn("flex items-center justify-end gap-2 px-4 py-3 mt-4 border-t", actionsClassName)}>
          <Button
            variant="secondary"
            size="xs"
            onClick={handleCancel}
            disabled={disabled}
          >
            {cancelButtonText}
          </Button>
          <Button
            size="xs"
            onClick={handleApply}
            disabled={disabled}
          >
            {applyButtonText}
          </Button>
        </div>
      )}
    </div>
  )
}

function InlineStringEditor({ value: initial, placeholder, onSave, onCancel, disabled }: {
  value: string
  placeholder?: string
  onSave: (value: string) => void
  onCancel: () => void
  disabled?: boolean
}) {
  const [value, setValue] = React.useState(initial)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    // focus when shown
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const commit = React.useCallback(() => {
    onSave(value.trim())
  }, [onSave, value])

  const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      commit()
    } else if (e.key === "Escape") {
      e.preventDefault()
      onCancel()
    }
  }, [commit, onCancel])

  return (
    <div className="flex items-center gap-2 w-full min-w-0">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="flex-1 min-w-0"
        disabled={disabled}
      />
      <div className="flex items-center gap-1 flex-shrink-0">
        <IconButton size="sm" onClick={commit} disabled={disabled} aria-label="Save">
          <Check className="h-4 w-4" />
        </IconButton>
        <IconButton size="sm" onClick={onCancel} disabled={disabled} aria-label="Cancel">
          <X className="h-4 w-4" />
        </IconButton>
      </div>
    </div>
  )
}
